import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Card do Trakd · superfície base com glassmorphism.
 *
 * Variants:
 *  - default   — glass sutil (bg-surface/60 backdrop-blur-xl)
 *  - elevated  — surface mais opaca, sombra mais forte
 *  - highlight — borda violet + glow brand (pra destacar)
 *  - ghost     — transparente com hover sutil (pra cards clicáveis)
 */
const cardVariants = cva(['relative rounded-3xl overflow-hidden transition-all duration-300'], {
  variants: {
    variant: {
      default: 'bg-surface/60 backdrop-blur-xl hover:bg-surface/75',
      elevated: 'bg-surface-elevated/70 backdrop-blur-xl shadow-trakd-card',
      highlight: 'bg-violet/[0.10] backdrop-blur-xl ring-1 ring-violet/30 shadow-trakd-glow-subtle',
      ghost: 'bg-transparent hover:bg-surface-elevated/40',
    },
    padding: {
      none: 'p-0',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'md',
  },
});

export type CardVariant = NonNullable<VariantProps<typeof cardVariants>['variant']>;

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
  as?: 'div' | 'article' | 'section';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant, padding, as: Component = 'div', ...props },
  ref,
) {
  return (
    <Component
      ref={ref as never}
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  );
});

// Header/Title/Body helpers seguindo o padrão shadcn (cada um é só um wrapper semântico)
export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cn('mb-4 flex flex-col gap-1', className)} {...props} />;
  },
);

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...props }, ref) {
    return (
      <h3
        ref={ref}
        className={cn('text-ink text-lg leading-tight font-semibold tracking-tight', className)}
        {...props}
      />
    );
  },
);

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn('text-ink-muted text-sm', className)} {...props} />;
});

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('text-ink', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('border-border-subtle mt-6 flex items-center gap-2 border-t pt-4', className)}
        {...props}
      />
    );
  },
);

export { cardVariants };
